import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCan } from "@/lib/authz";
import { Plus } from "@phosphor-icons/react";
import ScopeFilterBar, { scopeToQuery } from "@/components/ScopeFilterBar";

const CLS = {
  Pending: "bg-amber-50 text-amber-700 border border-amber-200",
  Accepted: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border border-red-200",
};
const OFFER_STATUSES = ["Draft", "Awaiting Offer Letter", "Pending SDL Approval", "Pending HR Approval", "Approved", "Accepted", "Rejected"];

export default function Offers() {
  const { user } = useAuth();
  const role = user?.role;
  // Phase A/B/C/D — permission-driven create gate. Legacy role filter kept as
  // secondary safety net since GL/Manager remain supervisory (not ICs).
  const canCreate = useCan("candidate.edit") &&
    ["admin", "super_admin", "team_lead", "senior_executive", "executive", "sde"].includes(role);
  const [items, setItems] = useState([]);
  const [cands, setCands] = useState([]);
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [form, setForm] = useState({ candidate_id: "", company: "", job_title: "", salary: "", offer_date: new Date().toISOString().slice(0, 10), joining_date: "", status: "Pending" });

  const load = useCallback(() => {
    const p = new URLSearchParams(scopeToQuery(filters)).toString();
    return api.get(`/offers${p ? `?${p}` : ""}`).then((r) => setItems(r.data));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/candidates").then((r) => setCands(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/offers", form); toast.success("Offer saved"); setOpen(false); load(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="offers-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Offers</h2>
          <p className="text-sm text-slate-500">Offers received by candidates.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-offer-button"
                disabled={!canCreate}
                title={!canCreate ? "View-only for your role" : ""}
                className={`text-sm px-4 py-2 rounded-md flex items-center gap-2 ${canCreate ? "bg-sky-600 hover:bg-sky-700 text-white" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}>
          <Plus size={16} /> Add Offer
        </button>
      </div>

      <ScopeFilterBar value={filters} onChange={setFilters}
                      candidates={cands}
                      showStatus statusOptions={OFFER_STATUSES} statusLabel="Offer Status"
                      testid="offers-scope" />

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Offer Date</th>
              <th className="text-left px-4 py-3">Candidate</th>
              <th className="text-left px-4 py-3">Company / Role</th>
              <th className="text-right px-4 py-3">Salary</th>
              <th className="text-left px-4 py-3">Joining</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => {
              const c = cands.find((x) => x.id === o.candidate_id);
              return (
                <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs text-slate-500">{o.offer_date}</td>
                  <td className="px-4 py-3">{c?.full_name || "—"}</td>
                  <td className="px-4 py-3"><b>{o.company}</b><div className="text-xs text-slate-500">{o.job_title}</div></td>
                  <td className="px-4 py-3 text-right tabular-nums">{o.salary}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{o.joining_date || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${CLS[o.status]}`}>{o.status}</span></td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-10">No offers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && canCreate && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-lg">
            <h3 className="font-semibold mb-4">Add Offer</h3>
            <div className="space-y-3">
              <select required value={form.candidate_id} onChange={(e) => setForm((f) => ({ ...f, candidate_id: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                <option value="">Select candidate…</option>
                {cands.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input required placeholder="Job Title" value={form.job_title} onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input required placeholder="Salary" value={form.salary} onChange={(e) => setForm((f) => ({ ...f, salary: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input type="date" value={form.offer_date} onChange={(e) => setForm((f) => ({ ...f, offer_date: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input type="date" placeholder="Joining Date" value={form.joining_date} onChange={(e) => setForm((f) => ({ ...f, joining_date: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option>Pending</option><option>Accepted</option><option>Rejected</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
